import apiService from './api';

export interface AccessionConfig {
  part1Type: string;
  part1Custom: string;
  part2Type: string;
  part2Custom: string;
  part3Type: string;
  part3Custom: string;
}

export interface AccessionGenerationParams {
  locationId: string;
  patientId?: string;
  customYear?: number;
}

export class AccessionService {
  /**
   * Generate an accession number based on location configuration
   */
  static async generateAccessionNumber(params: AccessionGenerationParams): Promise<string> {
    try {
      // Get location configuration
      const location = await apiService.getLocation(params.locationId);
      
      if (!location) {
        throw new Error('Location not found');
      }

      const parts: string[] = [];
      const year = params.customYear || new Date().getFullYear();
      
      // Generate Part 1
      parts.push(this.generatePart(
        location.accessionPart1Type || 'custom',
        location.accessionPart1Custom || '',
        year,
        params.patientId,
        location.labCode
      ));
      
      // Generate Part 2
      parts.push(this.generatePart(
        location.accessionPart2Type || 'year',
        location.accessionPart2Custom || '',
        year,
        params.patientId,
        location.labCode
      ));
      
      // Generate Part 3
      parts.push(this.generatePart(
        location.accessionPart3Type || 'patientId',
        location.accessionPart3Custom || '',
        year,
        params.patientId,
        location.labCode
      ));
      
      // Add random 5-character suffix
      const randomSuffix = this.generateRandomSuffix();
      
      return parts.join('_') + '_' + randomSuffix;
    } catch (error) {
      console.error('Error generating accession number:', error);
      throw error;
    }
  }

  /**
   * Generate a single part of the accession number
   */
  private static generatePart(
    type: string,
    custom: string,
    year: number,
    patientId?: string,
    locationCode?: string
  ): string {
    switch (type) {
      case 'custom':
        return custom || 'ABC';
      case 'year':
        return year.toString();
      case 'patientId':
        return patientId || '12345';
      case 'locationCode':
        return locationCode || 'LOC';
      default:
        return 'ABC';
    }
  }

  /**
   * Generate a random 5-character suffix
   */
  private static generateRandomSuffix(): string {
    // Generate a random string using a more reliable method
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 5; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /**
   * Validate accession configuration
   */
  static validateConfig(config: AccessionConfig): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validate Part 1
    if (config.part1Type === 'custom' && (!config.part1Custom || config.part1Custom.length < 1)) {
      errors.push('Part 1 custom text must be at least 1 character');
    }
    if (config.part1Type === 'custom' && config.part1Custom && config.part1Custom.length > 5) {
      errors.push('Part 1 custom text must be 5 characters or less');
    }

    // Validate Part 2
    if (config.part2Type === 'custom' && (!config.part2Custom || config.part2Custom.length < 1)) {
      errors.push('Part 2 custom text must be at least 1 character');
    }
    if (config.part2Type === 'custom' && config.part2Custom && config.part2Custom.length > 5) {
      errors.push('Part 2 custom text must be 5 characters or less');
    }

    // Validate Part 3
    if (config.part3Type === 'custom' && (!config.part3Custom || config.part3Custom.length < 1)) {
      errors.push('Part 3 custom text must be at least 1 character');
    }
    if (config.part3Type === 'custom' && config.part3Custom && config.part3Custom.length > 5) {
      errors.push('Part 3 custom text must be 5 characters or less');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Generate a preview accession number for configuration testing
   */
  static generatePreview(config: AccessionConfig): string {
    const parts: string[] = [];
    const year = new Date().getFullYear();
    
    // Generate Part 1
    parts.push(this.generatePart(
      config.part1Type,
      config.part1Custom,
      year,
      '12345',
      'LOC'
    ));
    
    // Generate Part 2
    parts.push(this.generatePart(
      config.part2Type,
      config.part2Custom,
      year,
      '12345',
      'LOC'
    ));
    
    // Generate Part 3
    parts.push(this.generatePart(
      config.part3Type,
      config.part3Custom,
      year,
      '12345',
      'LOC'
    ));
    
    // Add random 5-character suffix
    const randomSuffix = this.generateRandomSuffix();
    
    return parts.join('_') + '_' + randomSuffix;
  }
}

export default AccessionService; 